import { useMemo } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { MessageCircle, Bot } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PrivateConversationSummary } from '@/types/message'
import type { UnreadCounts } from '@/hooks/useUnreadCounts'

interface PrivateInboxProps {
  conversations: PrivateConversationSummary[]
  masterUserId?: string
  unreadCounts?: UnreadCounts
  isUserOnline?: (userId: string) => boolean
  loading?: boolean
  onOpenConversation: (conversation: PrivateConversationSummary) => void
}

export function PrivateInbox({
  conversations,
  masterUserId,
  unreadCounts = {},
  isUserOnline,
  loading = false,
  onOpenConversation
}: PrivateInboxProps) {
  // Presença: NPC não conecta (userId sintético) — herda a do controlador/mestre
  const isConversationOnline = (conv: PrivateConversationSummary): boolean => {
    if (!isUserOnline) return false
    const presenceId = conv.is_npc
      ? (conv.controlled_by_user_id || masterUserId || conv.user_id)
      : conv.user_id
    return isUserOnline(presenceId)
  }

  const unreadOf = (conv: PrivateConversationSummary): number =>
    unreadCounts[conv.user_id] ?? conv.unread_count ?? 0

  // Ordenar: não lidas primeiro, depois conversas mais recentes, depois alfabético
  const sorted = useMemo(() => {
    return [...conversations].sort((a, b) => {
      const aUnread = (unreadCounts[a.user_id] ?? a.unread_count ?? 0) > 0 ? 1 : 0
      const bUnread = (unreadCounts[b.user_id] ?? b.unread_count ?? 0) > 0 ? 1 : 0
      if (aUnread !== bUnread) return bUnread - aUnread

      const aTime = a.last_message_timestamp ? Date.parse(a.last_message_timestamp) : 0
      const bTime = b.last_message_timestamp ? Date.parse(b.last_message_timestamp) : 0
      if (aTime !== bTime) return bTime - aTime

      return a.user_full_name.localeCompare(b.user_full_name, 'pt-BR')
    })
  }, [conversations, unreadCounts])

  return (
    <div className="h-full bg-game-secondary dark:bg-zinc-900/95 flex flex-col">
      <div className="p-4 border-b border-game-border-primary dark:border-zinc-800">
        <p className="text-xs uppercase tracking-[0.2em] text-game-muted dark:text-zinc-500 mb-1">Privado</p>
        <h3 className="font-semibold text-lg text-game-primary dark:text-zinc-100">Conversas</h3>
        <p className="text-xs text-game-muted dark:text-zinc-400 mt-1">
          Apenas você e o destinatário veem estas mensagens.
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {sorted.map((conv) => {
            const isOnline = isConversationOnline(conv)
            const unread = unreadOf(conv)
            const isMasterConv = !conv.is_npc && conv.user_id === masterUserId

            return (
              <button
                key={conv.user_id}
                onClick={() => onOpenConversation(conv)}
                className="w-full text-left group px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/[0.05] border border-transparent hover:border-violet-500/30"
              >
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-10 w-10 ring-1 ring-zinc-700/50">
                      {conv.is_npc ? (
                        <AvatarFallback className="bg-purple-600/30 text-purple-400">
                          <Bot className="h-5 w-5" />
                        </AvatarFallback>
                      ) : (
                        <AvatarFallback className="bg-game-tertiary dark:bg-zinc-800 text-game-secondary dark:text-zinc-300 text-sm font-medium">
                          {conv.user_full_name[0]?.toUpperCase() || '?'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    {isUserOnline && (
                      <span
                        className={cn(
                          'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-game-secondary dark:border-zinc-900',
                          isOnline ? 'bg-emerald-500' : 'bg-zinc-500'
                        )}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm text-game-primary dark:text-zinc-100 truncate">
                        {conv.user_full_name}
                      </span>
                      {conv.is_npc && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-purple-600/20 text-purple-400 border-purple-500/30">
                          NPC
                        </Badge>
                      )}
                      {isMasterConv && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-yellow-600/20 text-yellow-400 border-yellow-500/30">
                          Mestre
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-game-muted dark:text-zinc-400 truncate">
                      {conv.last_message || 'Sem mensagens ainda'}
                    </p>
                  </div>

                  {unread > 0 && (
                    <div className="flex-shrink-0 min-w-[20px] h-5 px-1.5 bg-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-[11px] font-bold text-white leading-none">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            )
          })}

          {!loading && sorted.length === 0 && (
            <div className="text-center py-12 px-4 text-game-muted">
              <div className="w-12 h-12 mx-auto bg-game-tertiary dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-3">
                <MessageCircle className="h-6 w-6 text-game-muted" />
              </div>
              <p className="text-sm">Nenhum participante para conversar</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
